import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import BadgeService from '../../../services/badgeService';

const BadgeListItem = ({ badge, onUpdate, onClick }) => {
  const [loading, setLoading] = useState(false);

  const handleToggleStatus = async (e) => {
    e?.stopPropagation(); // Prevent triggering the card click
    setLoading(true);
    try {
      const { error } = await BadgeService?.toggleBadgeStatus(badge?.id);
      if (error) {
        console.error('Error toggling badge status:', error);
      } else {
        onUpdate?.(); // Refresh the list
      }
    } catch (error) {
      console.error('Failed to toggle badge status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e) => {
    e?.stopPropagation(); // Prevent triggering the card click
    
    if (!window.confirm(`Are you sure you want to delete "${badge?.title}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const { error } = await BadgeService?.deleteBadge(badge?.id);
      if (error) {
        alert(error);
      } else {
        onUpdate?.(); // Refresh the list
      }
    } catch (error) {
      console.error('Failed to delete badge:', error);
      alert('Failed to delete badge');
    } finally {
      setLoading(false);
    }
  };

  const formatCategory = (category) => {
    return category?.charAt(0)?.toUpperCase() + category?.slice(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'learning': return 'BookOpen';
      case 'streak': return 'Zap';
      case 'mastery': return 'Star';
      case 'special': return 'Crown';
      default: return 'Award';
    }
  };

  return (
    <div 
      onClick={() => onClick?.(badge)}
      className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 hover:bg-opacity-15 transition-all duration-200 cursor-pointer group"
    >
      <div className="flex items-start justify-between">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-4">
            {/* Badge Icon/Image */}
            <div className="flex-shrink-0">
              {badge?.image_url ? (
                <img 
                  src={badge?.image_url} 
                  alt={badge?.title}
                  className="w-12 h-12 rounded-lg object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <Icon 
                    name={getCategoryIcon(badge?.category)} 
                    size={24} 
                    className="text-blue-200" 
                  />
                </div>
              )}
            </div>

            {/* Badge Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold text-white truncate group-hover:text-blue-100 transition-colors">
                  {badge?.title}
                </h3>
                
                {/* Status Badge */}
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  badge?.is_active 
                    ? 'bg-green-500 bg-opacity-20 text-green-300 border border-green-500 border-opacity-30' :'bg-red-500 bg-opacity-20 text-red-300 border border-red-500 border-opacity-30'
                }`}>
                  {badge?.is_active ? 'Active' : 'Inactive'}
                </div>

                {/* Category Badge */}
                <div className="px-2 py-1 bg-blue-500 bg-opacity-20 text-blue-300 border border-blue-500 border-opacity-30 rounded-full text-xs font-medium">
                  <Icon name={getCategoryIcon(badge?.category)} size={12} className="inline mr-1" />
                  {formatCategory(badge?.category)}
                </div>
              </div>

              <p className="text-blue-100 text-sm mb-3 line-clamp-2">
                {badge?.description}
              </p>

              {/* Badge Stats */}
              <div className="flex items-center gap-6 text-sm text-blue-200">
                <div className="flex items-center gap-1">
                  <Icon name="Users" size={14} />
                  <span>{badge?.assignment_count || 0} assigned</span>
                </div>
                
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={14} />
                  <span>{badge?.points || 0} points</span>
                </div>
                
                <div className="flex items-center gap-1">
                  <Icon name="Calendar" size={14} />
                  <span>Created {formatDate(badge?.created_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 ml-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleStatus}
            disabled={loading}
            iconName={badge?.is_active ? 'EyeOff' : 'Eye'}
            className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white rounded-lg"
            title={badge?.is_active ? 'Deactivate badge' : 'Activate badge'}
          />
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            disabled={loading || (badge?.assignment_count > 0)}
            iconName="Trash2"
            className="border-red-400 text-red-400 hover:bg-red-400 hover:text-white rounded-lg"
            title={badge?.assignment_count > 0 ? 'Cannot delete badge with assignments' : 'Delete badge'}
          />
          
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <Icon name="ChevronRight" size={20} className="text-blue-200" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgeListItem;